import { Checkbox, Grid, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';
import Loading from 'components/Loading';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import i18n from 'i18n';
import PermissionCard from 'containers/Groups/PermissionCard';
import Alert from '@material-ui/lab/Alert';
import styles from '../../Template/styles';
import { ENTITY_NAME } from './Content';

const useStyles = makeStyles(styles);

interface Props {
  title: string;
  subtitle: string;
  isLoading: boolean;
  permissions?: {
    loading: boolean;
    selected: string[];
    available: { name: string; selected: boolean }[];
  };
  pending: string[];
  setPending: (pending: string[]) => void;
  updatePermissions: (permissions: string[]) => void;
}

const ListPermissions = ({
  title,
  subtitle,
  permissions,
  pending,
  isLoading,
  setPending,
  updatePermissions,
}: Props) => {
  const classes = useStyles();
  const [checked, setChecked] = React.useState<string[]>([]);

  const permissionsNames: string[] = permissions?.available?.map(
    item => item.name
  ) || [''];

  const permissionList = [
    {
      key: 'user',
      label: i18n.t('USERS_KEY'),
      values: [
        'list_users',
        'edit_user_details',
        'activate_user',
        'can_import_users_in_all_clients',
        'create_new_user',
        'delete_user',
        'delete_user_permanently',
        'inactivate_user',
        'list_session_history',
        'reset_user_mfa',
        'reset_user_password',
        'users_recovery',
        'activate_user_reset_password_at_login',
        'inactivate_user_reset_password_at_login',
        'disable_user_devices',
        'list_user_devices',
        'enable_device_validation_bypass',
        'disable_device_validation_bypass',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'application',
      label: i18n.t('APPLICATIONS_KEY'),
      values: [
        'include_application',
        'list_applications',
        'list_available_applications',
        'list_included_applications',
        'can_simulate_applications',
        'create_new_application',
        'edit_application',
        'delete_application',
        'remove_included_application',
        'create_new_web_application',
        'list_web_applications',
        'edit_web_application',
        'delete_web_application',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'client',
      label: i18n.t('CLIENTS_KEY'),
      values: [
        'list_client',
        'edit_client',
        'delete_client',
        'client_access_control',
        'clone_client',
        'edit_iframe_max_length',
        'list_iframe_max_length',
        'can_export_client_summary_list',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'log',
      label: i18n.t('LOGS_KEY'),
      values: ['list_client_logs'].filter(item =>
        permissionsNames.includes(item)
      ),
    },
    {
      key: 'alert',
      label: i18n.t('GROUPS_PAGE.ALERTS_KEY'),
      values: [
        'list_client_scheduled_warnings',
        'edit_client_scheduled_warnings',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'maintenance',
      label: i18n.t('MAINTENANCE_KEY'),
      values: [
        'get_client_maintenance',
        'edit_client_maintenance',
        'client_maintenance_history',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'sessions',
      label: i18n.t('SESSIONS_KEY'),
      values: [
        'list_client_instance_sessions',
        'delete_client_instance_sessions',
      ].filter(item => permissionsNames.includes(item)),
    },
  ].filter(item => item.values.length > 0);

  const handleCheck = {
    exists: (value: string) => checked.includes(value),
    toggle: (value: string) => {
      setPending(
        pending.includes(value)
          ? pending.filter(item => item !== value)
          : pending.concat(value)
      );

      const newCheckeds = checked.includes(value)
        ? checked.filter(item => item !== value)
        : [...checked, value];

      setChecked(newCheckeds);
      updatePermissions(newCheckeds);
    },
  };

  React.useEffect(() => {
    if (permissions?.selected) {
      setChecked(permissions.selected);
    }
  }, [permissions]);

  const allPermissionsAvailable = React.useMemo(() => permissionsNames || [], [
    permissionsNames,
  ]);

  const handleCheckAllExists = (values: string[]) =>
    values.reduce(
      (permissionsResponse, currentValue) =>
        permissionsResponse && checked.includes(currentValue),
      true
    );

  const handleToggleAll = (values: string[]) => {
    let newListPending = [...pending];
    values.forEach(value => {
      if (newListPending.includes(value)) {
        newListPending = newListPending.filter(item => item !== value);
      } else {
        newListPending = [...newListPending, value];
      }
    });
    setPending(newListPending);
    if (handleCheckAllExists(values)) {
      setChecked([]);
      updatePermissions([]);
    } else {
      setChecked(values);
      updatePermissions(values);
    }
  };

  return (
    <React.Fragment>
      {permissionList.length === 0 ? (
        <Grid container justify="center" className={classes.warning}>
          <Alert className={classes.alert} severity="warning" color="info">
            <Grid item>
              <Typography>{i18n.t('NO_PERMISSIONS_IN_GROUP_KEY')}</Typography>
            </Grid>
          </Alert>
        </Grid>
      ) : (
        <Grid
          container
          spacing={2}
          className={clsx({
            [classes.disableArea]: permissions?.loading,
          })}
        >
          <Grid item xs={12} style={{ margin: '32px 0 8px' }}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2">{subtitle}</Typography>
          </Grid>
          {(permissions?.loading && !permissionsNames) || isLoading ? (
            <Loading />
          ) : (
            <>
              <Grid container className={classes.superTitle}>
                <Checkbox
                  size="small"
                  checked={handleCheckAllExists(allPermissionsAvailable)}
                  onChange={() => {
                    handleToggleAll(allPermissionsAvailable);
                  }}
                  className={classes.checkIcon}
                />
                <Typography className={classes.titleText}>
                  {i18n.t('GROUPS_PAGE.SELECT_ALL_KEY')}
                </Typography>
              </Grid>
              <PermissionCard
                groupPermissions={permissionList}
                entityKey="client"
                permissions={permissions}
                pending={pending}
                handleCheck={handleCheck}
                handleCheckAllExists={handleCheckAllExists}
                setPending={setPending}
                updatePermissions={updatePermissions}
                checked={checked}
                setChecked={setChecked}
              />
            </>
          )}
        </Grid>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = ({
  permission: { permissions, isLoading },
}: ApplicationState) => ({
  permissions: permissions[ENTITY_NAME],
  isLoading,
});

export default connect(mapStateToProps)(ListPermissions);
